import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';

// Déclaration TypeScript pour gtag
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

export function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Toujours activé
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Vérifier si l'utilisateur a déjà donné son consentement
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      // Attendre 1 seconde avant d'afficher la bannière
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Charger les préférences sauvegardées
      try {
        const savedPreferences = JSON.parse(consent);
        setPreferences(savedPreferences);
      } catch (e) {
        console.error('Error parsing cookie preferences:', e);
      }
    }
  }, []);

  const acceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookie-consent', JSON.stringify(allAccepted));
    setShowBanner(false);
    setShowSettings(false);
    
    // Activer Google Analytics si accepté
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: 'granted'
      });
    }
  };

  const acceptEssential = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false
    };
    setPreferences(essentialOnly);
    localStorage.setItem('cookie-consent', JSON.stringify(essentialOnly));
    setShowBanner(false);
    setShowSettings(false);
    
    // Refuser Google Analytics
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied'
      });
    }
  };

  const savePreferences = () => {
    localStorage.setItem('cookie-consent', JSON.stringify(preferences));
    setShowBanner(false);
    setShowSettings(false);
    
    // Mettre à jour le consentement Google Analytics
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: preferences.analytics ? 'granted' : 'denied',
        ad_storage: preferences.marketing ? 'granted' : 'denied'
      });
    }
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
      >
        <Card className="max-w-6xl mx-auto bg-background/95 backdrop-blur-lg border-2 shadow-2xl">
          {!showSettings ? (
            // Bannière principale
            <div className="p-6 md:p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 bg-primary/10 rounded-xl flex-shrink-0">
                  <Cookie className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl md:text-2xl font-bold mb-3">
                    Nous utilisons des cookies 🍪
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Nous utilisons des cookies pour améliorer votre expérience de navigation, analyser le trafic du site et personnaliser le contenu. 
                    En cliquant sur "Tout accepter", vous consentez à l'utilisation de TOUS les cookies. 
                    Vous pouvez également personnaliser vos préférences.
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Pour en savoir plus, consultez notre{' '}
                    <a href="/#/politique-confidentialite" className="text-primary hover:underline font-semibold">
                      Politique de Confidentialité
                    </a>
                  </p>
                </div>
                <button
                  onClick={acceptEssential}
                  className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                  aria-label="Fermer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  onClick={acceptAll}
                  className="flex-1 sm:flex-none"
                >
                  Tout accepter
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={acceptEssential}
                  className="flex-1 sm:flex-none"
                >
                  Cookies essentiels uniquement
                </Button>
                <Button
                  size="lg"
                  variant="ghost"
                  onClick={() => setShowSettings(true)}
                  className="flex-1 sm:flex-none"
                >
                  <Settings className="mr-2 w-4 h-4" />
                  Personnaliser
                </Button>
              </div>
            </div>
          ) : (
            // Panneau de paramètres
            <div className="p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-primary/10 rounded-xl">
                    <Settings className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold">
                    Paramètres des cookies
                  </h3>
                </div>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Retour"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Cookies essentiels */}
                <div className="flex items-start justify-between gap-4 p-4 bg-muted/50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold">Cookies essentiels</h4>
                      <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full font-semibold">
                        Obligatoire
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Ces cookies sont nécessaires au fonctionnement du site et ne peuvent pas être désactivés. 
                      Ils permettent la navigation, la sécurité et l'accès aux fonctionnalités de base.
                    </p>
                  </div>
                  <Switch
                    checked={preferences.essential}
                    disabled
                    className="flex-shrink-0"
                  />
                </div>

                {/* Cookies analytiques */}
                <div className="flex items-start justify-between gap-4 p-4 bg-muted/50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-semibold mb-2">Cookies analytiques</h4>
                    <p className="text-sm text-muted-foreground">
                      Ces cookies nous aident à comprendre comment les visiteurs utilisent notre site. 
                      Ils collectent des informations anonymes sur les pages visitées et les interactions.
                    </p>
                  </div>
                  <Switch
                    checked={preferences.analytics}
                    onCheckedChange={(checked) => 
                      setPreferences({ ...preferences, analytics: checked })
                    }
                    className="flex-shrink-0"
                  />
                </div>

                {/* Cookies marketing */}
                <div className="flex items-start justify-between gap-4 p-4 bg-muted/50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-semibold mb-2">Cookies marketing</h4>
                    <p className="text-sm text-muted-foreground">
                      Ces cookies sont utilisés pour afficher des publicités pertinentes et suivre l'efficacité 
                      de nos campagnes marketing sur les réseaux sociaux et autres plateformes.
                    </p>
                  </div>
                  <Switch
                    checked={preferences.marketing}
                    onCheckedChange={(checked) => 
                      setPreferences({ ...preferences, marketing: checked })
                    }
                    className="flex-shrink-0"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  onClick={savePreferences}
                  className="flex-1"
                >
                  Enregistrer mes préférences
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={acceptAll}
                  className="flex-1"
                >
                  Tout accepter
                </Button>
              </div>
            </div>
          )}
        </Card>
      </motion.div>
    </AnimatePresence>
  );
}
